import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { listSubscriptions, upsertSubscription } from '../services/operationsReportsApi';
import { useAuthStore } from '../store/authStore';
import type { SubscriptionRow } from '../types/operationsReports';

const STALE_TIME_MS = 5 * 60 * 1000;

export function useEmailSubscriptions() {
  const queryClient = useQueryClient();
  const userId = useAuthStore((s) => s.user?.user_id);
  const queryKey = ['operations-reports', 'subscriptions', userId] as const;

  const query = useQuery({
    queryKey,
    queryFn: () => listSubscriptions().then((r) => r.subscriptions),
    enabled: !!userId,
    staleTime: STALE_TIME_MS,
  });

  const mutation = useMutation({
    mutationFn: ({ siteId, enabled }: { siteId: string; enabled: boolean }) =>
      upsertSubscription(siteId, enabled),
    onMutate: async ({ siteId, enabled }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<SubscriptionRow[]>(queryKey) ?? [];
      const next = applyToggle(previous, siteId, enabled);
      queryClient.setQueryData(queryKey, next);
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous !== undefined) {
        queryClient.setQueryData(queryKey, ctx.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  return {
    subscriptions: query.data ?? [],
    isLoading: query.isLoading,
    isToggling: mutation.isPending,
    error: mutation.error ? mutation.error.message : null,
    toggle: (siteId: string, enabled: boolean) => mutation.mutate({ siteId, enabled }),
  };
}

/** Insert or update an entry for siteId in the subscriptions array. */
function applyToggle(prev: SubscriptionRow[], siteId: string, enabled: boolean): SubscriptionRow[] {
  const idx = prev.findIndex((s) => s.site_id === siteId);
  if (idx >= 0) {
    const next = prev.slice();
    next[idx] = { ...prev[idx], enabled, updated_at: new Date().toISOString() };
    return next;
  }
  // New subscription not yet in cache — insert with a synthetic id (will be replaced on invalidate)
  return [
    ...prev,
    {
      subscription_id: `optimistic-${siteId}`,
      site_id: siteId,
      enabled,
      updated_at: new Date().toISOString(),
    },
  ];
}
